/**
 * MyPlanPage — AE + BDR landing page after login.
 *
 * Sections:
 *   1. Header strip: name, role, manager, sprint window banner, days remaining.
 *   2. Goals tree (read-only annual/quarter/sprint, editable month/week).
 *   3. This week activity targets vs actuals with PaceChip per row.
 *   4. Hunt list (sprint-target accounts).
 *   5. Sprint pipeline strip (DualGoalProgressBar - reused from sprint integration).
 *   6. Coaching notes feed (latest 3, rep-visible only).
 *   7. DailyReconcileBox at the bottom.
 *
 * BDR variant (Michael): swaps revenue rows for handoff rows; activity table
 * substitutes "outbound calls / outbound emails / LinkedIn touches / first
 * meetings booked / quals handed off"; sprint strip shows handoff progress.
 */
import { useEffect, useState } from "react";
import { getSession, api } from "@/lib/session";
import { getImpersonationTarget } from "@/lib/impersonation";
import { useSprintConfig } from "@/lib/useSprintConfig";
import { useFunnelDefaults, activityTargetsForRevenue, elapsedFraction } from "@/lib/usePeriodMath";
import PlanGoalsTree, { type RepPlanRow, type ManagerCeilings } from "@/components/PlanGoalsTree";
import PaceChip from "@/components/PaceChip";
import DailyReconcileBox from "@/components/DailyReconcileBox";
import DualGoalProgressBar from "@/components/DualGoalProgressBar";
import OperatorShell from "@/components/operator/OperatorShell";

interface PlanPayload {
  ceilings: ManagerCeilings;
  month?: RepPlanRow | null;
  weeks: RepPlanRow[];
}

interface ActivityActuals {
  outreach: number;
  first_meetings: number;
  qualified_meetings: number;
  closed_won_count: number;
  qualified_handoffs: number;
  closed_won_amount: number;
}

interface CoachingNote {
  id: string;
  body: string;
  manager_name?: string;
  created_at: string;
}

interface HuntAccount {
  id: number;
  name: string;
  industry?: string | null;
  stage?: string | null;
}

const ZERO_ACT: ActivityActuals = {
  outreach: 0, first_meetings: 0, qualified_meetings: 0,
  closed_won_count: 0, qualified_handoffs: 0, closed_won_amount: 0,
};

function startOfWeekISO(d: Date = new Date()): string {
  const x = new Date(d);
  const day = x.getDay();
  const diff = (day === 0 ? -6 : 1 - day);
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x.toISOString().slice(0, 10);
}

function endOfWeekISO(d: Date = new Date()): string {
  const s = new Date(startOfWeekISO(d));
  s.setDate(s.getDate() + 6);
  return s.toISOString().slice(0, 10);
}

export default function MyPlanPage() {
  const session = getSession();
  // W66.X15: effective identity — impersonation target overrides real session for
  // display (name, role). Data fetches use api() which auto-sends X-Impersonate-User-Id.
  const impersonationTarget = getImpersonationTarget();
  const effectiveName = impersonationTarget?.name || session?.name;
  const effectiveRole = impersonationTarget?.role || session?.role;
  const isBdr = effectiveRole === "bdr";
  const { window: sprintWin } = useSprintConfig();
  const funnel = useFunnelDefaults();

  const [plan, setPlan] = useState<PlanPayload | null>(null);
  const [actuals, setActuals] = useState<ActivityActuals>(ZERO_ACT);
  const [hunt, setHunt] = useState<HuntAccount[]>([]);
  const [coaching, setCoaching] = useState<CoachingNote[]>([]);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const wkStart = startOfWeekISO();
        const monthStart = `${new Date().toISOString().slice(0, 7)}-01`;
        const [p, a, h, c] = await Promise.all([
          api<PlanPayload>(`/api/me/plan?period_type=month&start=${monthStart}`).catch(() => null),
          api<{ blended: ActivityActuals }>(`/api/me/activity?from=${wkStart}&to=${endOfWeekISO()}`).catch(() => null),
          api<{ accounts: HuntAccount[] }>(`/api/accounts?ownerId=${session?.id ?? ""}&sprint_target=true`).catch(() => null),
          api<{ notes: CoachingNote[] }>(`/api/coaching/notes?rep_user_id=${session?.id ?? ""}`).catch(() => null),
        ]);
        if (cancelled) return;
        if (p) setPlan(p);
        if (a?.blended) setActuals({ ...ZERO_ACT, ...a.blended });
        if (h?.accounts) setHunt(h.accounts.slice(0, 10));
        if (c?.notes) setCoaching(c.notes.slice(0, 3));
      } catch { /* tolerate */ }
    })();
    return () => { cancelled = true; };
  }, [session?.id, reload]);

  // Activity targets for the week
  const weekRev = isBdr ? 0 : (plan?.weeks?.[0]?.revenue_target ?? 0);
  const weekHandoffs = isBdr ? (plan?.weeks?.[0]?.qualified_handoffs_target ?? 0) : 0;
  const targets = activityTargetsForRevenue(weekRev * funnel.weeksPerMonth, funnel);
  // weekly target: divide annualized by weeks
  const weeklyTargets = {
    closes: weekRev / funnel.avgDealSize,
    quals: (weekRev / funnel.avgDealSize) / funnel.closeRateQualToWon,
    meetings: ((weekRev / funnel.avgDealSize) / funnel.closeRateQualToWon) * funnel.meetingsPerQual,
    outreach: ((weekRev / funnel.avgDealSize) / funnel.closeRateQualToWon) * funnel.meetingsPerQual * funnel.outreachPerMeeting,
  };
  const elapsed = elapsedFraction(startOfWeekISO(), endOfWeekISO());

  const ceilings: ManagerCeilings = plan?.ceilings ?? {
    annual: 0,
    quarter: 0,
    sprint: 0,
  };

  // F-04 (2026-05-16): wrap in OperatorShell so users have nav back home.
  return (
    <OperatorShell
      breadcrumbs={[{ label: "Home", href: "/" }, { label: "My Plan" }]}
      pageTitle={`${effectiveName ?? "Your"} plan`}
      pageMeta={`${isBdr ? "BDR" : "AE"} · ${sprintWin?.sprintLabel ?? "Sprint"} · ${sprintWin?.daysRemaining ?? 0} days remaining`}
    >
      <div className="mx-auto max-w-6xl px-4 py-6">
        {/* D-002: Header strip removed to avoid duplicate with OperatorShell pageTitle. Sprint dates shown inline. */}
        {sprintWin && (
          <div className="mb-5 text-xs text-neutral-500">
            Sprint window: {sprintWin.sprintStart} – {sprintWin.sprintEnd}
          </div>
        )}

        <div className="grid gap-5 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-5">
            {/* Goals tree */}
            <PlanGoalsTree
              ceilings={ceilings}
              monthPlan={plan?.month ?? null}
              weekPlans={plan?.weeks ?? []}
              bdrMode={isBdr}
              onSaved={() => setReload((x) => x + 1)}
            />

            {/* Activity table */}
            <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
              <h3 className="mb-3 font-medium" style={{ color: "#0B0B0F", fontFamily: "DM Sans, sans-serif" }}>
                This week activity
              </h3>
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-neutral-500">
                  <tr><th className="pb-2">Metric</th><th className="pb-2">Target</th><th className="pb-2">Actual</th><th className="pb-2">Pace</th></tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  <tr><td className="py-2">Outreach</td><td>{Math.ceil(weeklyTargets.outreach)}</td><td>{actuals.outreach}</td><td><PaceChip actualToDate={actuals.outreach} target={Math.ceil(weeklyTargets.outreach)} elapsedFraction={elapsed} /></td></tr>
                  <tr><td className="py-2">First meetings</td><td>{Math.ceil(weeklyTargets.meetings)}</td><td>{actuals.first_meetings}</td><td><PaceChip actualToDate={actuals.first_meetings} target={Math.ceil(weeklyTargets.meetings)} elapsedFraction={elapsed} /></td></tr>
                  <tr><td className="py-2">Qualified meetings</td><td>{Math.ceil(weeklyTargets.quals)}</td><td>{actuals.qualified_meetings}</td><td><PaceChip actualToDate={actuals.qualified_meetings} target={Math.ceil(weeklyTargets.quals)} elapsedFraction={elapsed} /></td></tr>
                  {!isBdr && (
                    <tr><td className="py-2">Closes</td><td>{(weeklyTargets.closes).toFixed(2)}</td><td>{actuals.closed_won_count}</td><td><PaceChip actualToDate={actuals.closed_won_count} target={Math.max(1, Math.round(weeklyTargets.closes))} elapsedFraction={elapsed} /></td></tr>
                  )}
                  {isBdr && (
                    <tr><td className="py-2">Qualified handoffs</td><td>{weekHandoffs}</td><td>{actuals.qualified_handoffs}</td><td><PaceChip actualToDate={actuals.qualified_handoffs} target={weekHandoffs} elapsedFraction={elapsed} /></td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Hunt list */}
            <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
              <h3 className="mb-3 font-medium" style={{ color: "#0B0B0F", fontFamily: "DM Sans, sans-serif" }}>
                Hunt list - sprint accounts
              </h3>
              {hunt.length === 0 ? (
                <p className="text-sm text-neutral-500">No sprint-target accounts assigned yet.</p>
              ) : (
                <ul className="divide-y divide-neutral-100 text-sm">
                  {hunt.map((a) => (
                    <li key={a.id} className="flex items-center justify-between py-2">
                      <a className="text-[#0B0B0F] hover:underline" href={`#/account/${a.id}`}>{a.name}</a>
                      <span className="text-xs text-neutral-500">{a.industry ?? ""} - {a.stage ?? ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="space-y-5">
            {/* Sprint strip */}
            <DualGoalProgressBar />

            {/* Coaching notes */}
            <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
              <h3 className="mb-3 font-medium" style={{ color: "#0B0B0F", fontFamily: "DM Sans, sans-serif" }}>
                Recent coaching notes
              </h3>
              {coaching.length === 0 ? (
                <p className="text-sm text-neutral-500">No notes yet.</p>
              ) : (
                <ul className="space-y-3 text-sm">
                  {coaching.map((n) => (
                    <li key={n.id} className="rounded border border-neutral-100 p-2">
                      <div className="text-xs text-neutral-500">{n.manager_name ?? "Manager"} - {new Date(n.created_at).toLocaleDateString()}</div>
                      <div className="text-neutral-800">{n.body}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Daily reconcile */}
        <div className="mt-5">
          <DailyReconcileBox bdrMode={isBdr} onSaved={() => setReload((x) => x + 1)} />
        </div>
      </div>
    </OperatorShell>
  );
}
